import { downgradeInjectable, UpgradeModule } from '@angular/upgrade/static';
import {
	BuildEnv,
	CommonInitializationService,
	DcBaseInitializationService,
	UuidService,
} from '@datachain/ui-sdk/common';

import * as _angular_ from 'angular';
import * as $ from 'jquery';
import { environment } from '../environments/environment';
import { ProjectStorage } from './core/project.storage';
import { NgExportTemplate } from './export-template/ng-export-template';
import { NgExpositions } from './expositions/ng-expositions';
import { NgCore } from './core/ng-core';
import { NgDepot } from './depot/ng-depot';
import { ConnectionStorage } from './core/connection.storage';

declare global {
	const angular: typeof _angular_;
}

function differentialModuleBootstrapping(buildEnv: BuildEnv) {
	if (buildEnv === BuildEnv.Dev || buildEnv === BuildEnv.MR) {
		const mdl = ['demosModule'];
		angular.module('dcApp').requires.push(...mdl);
		return;
	}
}

function bootstrapWithConfig(upgrade: UpgradeModule): void {
	const buildNumber =
		DcBaseInitializationService.getConfigInstance().buildNumber;
	differentialModuleBootstrapping(buildNumber);
	upgrade.bootstrap(document.body, ['dcApp'], {
		strictDi: true,
	});
}

function setKcAjsConstants(): void {
	angular.module('dcApp').constant('PROJECT_ROLE_NAME', 'DC_PROJECT');
	angular.module('dcApp').constant('ADMIN_ROLE_NAME', 'DC_ADMIN');
	angular.module('dcApp').constant('MEMBER_ROLE_NAME', 'DC_MEMBER');
	angular.module('dcApp').constant('CONSOLE_ROLE_NAME', 'DC_CONSO');
}

async function setEnvConfig(): Promise<unknown> {
	return new Promise(function (resolve, reject) {
		$.getJSON('./src/components/base/constants/versions.json', (conf: any) => {
			angular.module('dcApp').constant('APP_VERSION', conf.version);
			angular.module('dcApp').constant('APP_BUILD', conf.build);
			angular.module('dcApp').constant('APP_BUILD_DATE', conf.buildDate);

			// derives if debug is enabled based on build number, version and environment
			// debug is enabled by default in dev mode
			// this step should be performed before enabling/disabling logging
			DcBaseInitializationService.updateConfig(
				DcBaseInitializationService.getConfigInstance().setBuildNumber(
					conf.build,
					conf.version,
					environment.enableDebug
				)
			);

			// enables/disables logging based on currently known state of DcBaseInitializationService service
			angular.module('dcApp').config([
				'$logProvider',
				'$provide',
				function ($logProvider: any, $provide: any) {
					$provide.decorator('$log', [
						'$delegate',
						function ($delegate: any) {
							var origInfo = $delegate.info;
							var origLog = $delegate.log;
							var origWarn = $delegate.warn;

							const debugEnabled =
								DcBaseInitializationService.getConfigInstance().isDebugEnabled;

							console.info = debugEnabled ? console.info : () => {};
							console.debug = debugEnabled ? console.debug : () => {};
							console.warn = debugEnabled ? console.warn : () => {};

							$delegate.info = function () {
								if (debugEnabled) origInfo.apply(null, arguments);
							};

							$delegate.log = function () {
								if (debugEnabled) origLog.apply(null, arguments);
							};

							$delegate.warn = function () {
								if (debugEnabled) origWarn.apply(null, arguments);
							};

							return $delegate;
						},
					]);
				},
			]);
		}).then(resolve, () => reject('could not query "versions.json" file'));
	});
}

function exposeNgServicesToAjs(): void {
	angular
		.module('dcApp')
		.factory('ProjectStorage', downgradeInjectable(ProjectStorage) as any)
		.factory('ConnectionStorage', downgradeInjectable(ConnectionStorage) as any)
		.factory('NgExportTemplate', downgradeInjectable(NgExportTemplate) as any)
		.factory('NgExpositions', downgradeInjectable(NgExpositions) as any)
		.factory('NgDepot', downgradeInjectable(NgDepot) as any)
		.factory('UuidService', downgradeInjectable(UuidService) as any)
		.factory(
			'CommonInitializationService',
			downgradeInjectable(CommonInitializationService) as any
		)
		.factory('NgCore', downgradeInjectable(NgCore) as any);
}

export async function initApp(upgrade: UpgradeModule): Promise<void> {
	exposeNgServicesToAjs();
	await setEnvConfig();
	setKcAjsConstants();
	bootstrapWithConfig(upgrade);
}
